import { useEffect, useRef, useContext } from 'react';

import { useParams } from 'react-router-dom';

import { AppContext } from '../scripts/context'
import { Debounce } from '../scripts/utilities';
import { ManageFadeSvg, CheckFadeSvg, GetGemSize } from '../scripts/fadeSvg';

import RouteData from '../routeData.json';

import Media from './media'
import Main from './main'
import Detail from './detail'

export default function Page() {

    const { page } = useParams();
    const { section } = useParams();
    const { item } = useParams();

    //const navigate = useNavigate();

    const noScrollOnRouteChangeRef = useRef(false);

    const polyIdsAll = useRef();
    const polyRandomnessAll = useRef();

    const resizeFinishedTimeoutRef = useRef();

    const previousScrollTime = useRef(Date.now());

    const detailMediaLoaderTimeoutRef = useRef();

    //const sectionRef = useRef("top");

    const scrollVariables = useRef({
        scrollAnimFactorOffset: 0,
        scrollAnimFactorGemScale: 0,
        scrollAnimLengthGemScale: 0,
        scrollAnimStepGemScale: 0
    });

    const {
    //GENERAL
        setBodyHeight,
    //MENU
		handleMenu,
    //CONTACT CONTAINER
        handleContactContainer,
    //WIDGET
        setWidgetState,
    //SCROLL
        setScrollbarSliderStyleState,
        getScrollbarSliderStyle,
		scrollToOverview,
		scrollToTop,
        scrollToBottom,
        getScrollPosition,
        getFadeEndTop,
        getFadeStartBottom,
        getFadeEndTopPixel,
        getFadeStartBottomPixel,
    //DETAIL
        detailMediaLoadedRef,
        setDetailWidgetState,
        setDetailMediaLoaderState,
    //STYLE
        setMainContainerStyleState,
        getMainContainerVisibility,
        setMediaContainerOpacityState,
        getMediaContainerOpacity,
        setMediaContainerVisibilityState,
        getMediaContainerVisibility,
        getTitleContainerOpacity,
        getTitleContainerVisibility,
        setTitleContainerStyleState,
        updateCss,
    } = useContext(AppContext);

    function setScrollEffects(polys, randomness, scrollVariables) {

        const scrollPos = getScrollPosition();
        const fadeEndTop = getFadeEndTop();
        const fadeStartBottom = getFadeStartBottom();

        function setElementsAppearance() {

            setMainContainerStyleState(prevState => ({
                ...prevState,
                visibility: getMainContainerVisibility()
            }));

            setTitleContainerStyleState(prevState => ({
                ...prevState,
                visibility: getTitleContainerVisibility(),
                opacity: getTitleContainerOpacity()
            }));

            setMediaContainerOpacityState(getMediaContainerOpacity());
            setMediaContainerVisibilityState(getMediaContainerVisibility());

        };

        function setGemScale() {

            if (CheckFadeSvg()) {

                polys.forEach((polyIdsLine, indexLine) => {

                    /* calculate polyScale per line calculation based on: "low2 + (value - low1) * (high2 - low2) / (high1 - low1)" and "Math.min(Math.max(value, min), max)" */

                    let animPos;
                    let polyScale;
                    let fadeLinesTop = getFadeEndTopPixel() / GetGemSize();
                    let fadeLinesBottom = 1 + (getFadeStartBottomPixel() + window.innerWidth) / GetGemSize();

                    if (scrollPos < fadeEndTop && indexLine > fadeLinesTop) {

                        animPos = Math.min(Math.max((scrollPos / fadeEndTop), 0), 1);

                        /* fade in */
                        polyScale = Math.min(Math.max(((animPos - indexLine * scrollVariables.scrollAnimFactorGemScale) / scrollVariables.scrollAnimStepGemScale), 0), 1.02);

                    } else if (scrollPos > fadeStartBottom && indexLine < fadeLinesBottom) {

                        animPos = Math.min(Math.max(((scrollPos - fadeStartBottom) / (1 - fadeStartBottom)), 0), 1);

                        /* fade out */
                        polyScale = Math.min(Math.max((1 + -(animPos - indexLine * scrollVariables.scrollAnimFactorGemScale) / scrollVariables.scrollAnimStepGemScale), 0), 1.02);

                    } else {

                        polyScale = 1.02;

                    };

                    polyIdsLine.forEach((polyId, indexPoly) => {

                        let polyCurrent = document.getElementById(polyId);
                        let oldPolyScale = polyCurrent.style.transform;
                        //let oldPolyScale = polyCurrent.getAttribute("transform");

                        if (scrollPos < fadeEndTop || scrollPos > fadeStartBottom) {

                            let polyScaleRandom = (Math.min((polyScale * randomness[indexLine][indexPoly]), 1.02)).toFixed(3);

                            /* apply polyScale multiplied by randomFactor to element if changed */
                            if (oldPolyScale !== polyScaleRandom) {

                                polyCurrent.style.transform = "scale(" + polyScaleRandom + ")";
                                //polyCurrent.setAttribute("transform", "scale(" + polyScaleRandom + ")")

                            };

                        } else if (oldPolyScale !== 1.02) {

                            polyCurrent.style.transform = "scale(1.02)";
                            //polyCurrent.setAttribute("transform", "scale(1.02)")

                        };

                    });

                });

            };

        };

        setGemScale();
        setElementsAppearance();

    };

    function handleScroll() {

        if (Date.now() - previousScrollTime.current >= 16) {

            handleMenu("scroll");
            handleContactContainer("collapse");
            setDetailWidgetState(false);
            setWidgetState(false);
            
            setScrollEffects(polyIdsAll.current, polyRandomnessAll.current, scrollVariables.current);

            previousScrollTime.current = Date.now();

        };

        setScrollbarSliderStyleState(getScrollbarSliderStyle());

    };

    function handleScrollEnd() {

        handleMenu("scrollend");
        setScrollEffects(polyIdsAll.current, polyRandomnessAll.current, scrollVariables.current);

    };

    let resizeAwaitEnd;

    function handleResize() {

        //scroll to bottom if scroll position was at the bottom before resize (on expand height)
        clearTimeout(resizeAwaitEnd);
        resizeAwaitEnd = setTimeout(handleResizeEnd(), 20);

        function handleResizeEnd() {

            const scrollPos = getScrollPosition();

            if (scrollPos > 0.9) {
                scrollToBottom("instant");
            };
    
        };

        setBodyHeight();
        updateCss();
        Debounce(updateFadeSvg, 50);
        setScrollEffects(polyIdsAll.current, polyRandomnessAll.current, scrollVariables.current);

        //update fade svg and scroll effects on resize end, since update fade svg might not have been called on final size due to debounce
        clearTimeout(resizeFinishedTimeoutRef.current);
        resizeFinishedTimeoutRef.current = setTimeout(function(){
            updateFadeSvg();
            setScrollEffects(polyIdsAll.current, polyRandomnessAll.current, scrollVariables.current);
        }, 400);

    };

    function updateFadeSvg() {

        let manageFadeResult = ManageFadeSvg();
        polyIdsAll.current = manageFadeResult.polys;
        polyRandomnessAll.current = manageFadeResult.randomness;

        scrollVariables.current.scrollAnimLengthGemScale = 100;
        scrollVariables.current.scrollAnimFactorGemScale = 1 / (polyIdsAll.current.length + scrollVariables.current.scrollAnimLengthGemScale);
        scrollVariables.current.scrollAnimStepGemScale = scrollVariables.current.scrollAnimLengthGemScale * scrollVariables.current.scrollAnimFactorGemScale;

    };

    //useEffect on intial render
    useEffect(() => {

        setBodyHeight();
        updateFadeSvg();

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("scrollend", handleScrollEnd);
        window.addEventListener("resize", handleResize);

        return () => {

            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("scrollend", handleScrollEnd);
            window.removeEventListener("resize", handleResize);

        };
                                                                    // eslint-disable-next-line
    }, []);

    //useEffect on page, section or item parameter change
    useEffect(() => {

        setBodyHeight();
        updateFadeSvg();
        setScrollEffects(polyIdsAll.current, polyRandomnessAll.current, scrollVariables.current);

        // combine item, page, and section to get the path
        let path = `/${page || ""}${section ? "/" + section : ""}${item ? "/" + item : ""}`;

        // set document title to the title from RouteData
        const routeObj = RouteData.find((element) => element.path === path);
        document.title = routeObj ? routeObj.title : "TEST NOT FOUND - njb design®";

        setDetailMediaLoaderState(false);

        // Scroll depending on the route
        if (item || section === "nils") {

            // check if detail component media is loaded and wait if not
            if (detailMediaLoadedRef.current === true) {
                if (!noScrollOnRouteChangeRef.current) scrollToBottom("smooth");
                noScrollOnRouteChangeRef.current = false;
            } else {
                setDetailMediaLoaderState(true);
                clearTimeout(detailMediaLoaderTimeoutRef.current);
                let intervalDetailMediaLoaded = setInterval(checkDetailMediaLoaded, 50);
                function checkDetailMediaLoaded() {
                    if (detailMediaLoadedRef.current === true) {
                        clearInterval(intervalDetailMediaLoaded);
                        detailMediaLoaderTimeoutRef.current = setTimeout(function() {
                            if (!noScrollOnRouteChangeRef.current) scrollToBottom("smooth");
                            noScrollOnRouteChangeRef.current = false;
                            setDetailMediaLoaderState(false);
                        }, 250);
                    };
                };
            };

        } else if ((section || page === "about") && section !== "nils") {

            if (!noScrollOnRouteChangeRef.current) scrollToOverview("smooth");
            noScrollOnRouteChangeRef.current = false;

        } else {

            if (!noScrollOnRouteChangeRef.current) scrollToTop("smooth");
            noScrollOnRouteChangeRef.current = false;

        }
                                                                    // eslint-disable-next-line
    }, [page, section, item]);

    return (
        <main id="page">
            <Detail />
            <Media />
            <Main />
        </main>
    )

};